import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { Calendar, BookOpen, Dumbbell, Filter, TrendingUp, Trash2, ChevronRight, Clock, History, Pencil, X } from "lucide-react";

const MUSCLE_LABEL = {
  espalda: "Espalda", pecho: "Pecho", hombros: "Hombros", biceps: "Bíceps",
  triceps: "Tríceps", abdomen: "Abdomen", gluteos: "Glúteos", cuadriceps: "Cuádriceps",
  femoral: "Femoral", pantorrillas: "Pantorrillas", piernas: "Piernas", cardio: "Cardio",
  calistenia: "Calistenia",
};

// Render a short "stats" line for an entry depending on its mode (strength / bodyweight / cardio-rounds).
function entryStatsText(e) {
  if (e.cardio_rounds) {
    const parts = [`${e.cardio_rounds} rounds × ${e.cardio_duration_sec}s`];
    if (e.cardio_rest_sec != null) parts.push(`descanso ${e.cardio_rest_sec}s`);
    return parts.join(" · ");
  }
  const reps = parseReps(e.reps_per_set);
  const bits = [];
  if (e.is_bodyweight) bits.push("Peso corporal");
  else if (e.weight_kg != null) bits.push(`${e.weight_kg} kg`);
  bits.push(`${e.sets} series`);
  if (reps.length) bits.push(`reps ${reps.join("-")}`);
  return bits.join(" · ");
}

function parseReps(str) {
  try { return JSON.parse(str); } catch { return []; }
}

// Parse a "YYYY-MM-DD" string from the backend as a LOCAL date (midnight local time).
// Using `new Date("YYYY-MM-DD")` directly interprets it as UTC midnight, which
// shifts a day earlier in UTC-negative timezones (e.g. Cuba shows "01 oct" for "2026-10-02").
function localDateFromISODate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function fmtDay(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { weekday: "long" }); }
  catch { return "—"; }
}

export default function TrainingLog() {
  const [profile, setProfile] = useState(null);
  const [exercisesCatalog, setExercisesCatalog] = useState([]);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [weekIdx, setWeekIdx] = useState(3); // most recent
  const [openDay, setOpenDay] = useState(null);
  const [tab, setTab] = useState("semana"); // semana | historial | progreso
  const [filterMuscle, setFilterMuscle] = useState("");
  const [filterExercise, setFilterExercise] = useState("");
  const [progressExercise, setProgressExercise] = useState("");
  const [editing, setEditing] = useState(null); // entry object or null
  const [confirmDelete, setConfirmDelete] = useState(null); // entry object or null

  const load = async () => {
    try {
      const [p, s, l] = await Promise.all([
        api.get("/profile"),
        api.get("/training-log/summary"),
        api.get("/training-log?limit=1000"),
      ]);
      setProfile(p.data);
      setSummary(s.data);
      setEntries(l.data);
      const r = await api.get(`/content/exercises/${p.data.sex}`);
      setExercisesCatalog(r.data.exercises);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const sex = profile?.sex;
  const sexBadge = sex === "mujer"
    ? { label: "Mujer", icon: "♀", color: "text-[var(--mep-pink)] border-[var(--mep-pink)]/40 bg-[rgba(232,61,132,0.08)]" }
    : { label: "Hombre", icon: "♂", color: "text-[var(--mep-gold)] border-[var(--mep-gold)]/40 bg-[rgba(212,175,55,0.08)]" };

  const uniqueMuscles = useMemo(() => {
    const s = new Set(entries.map((e) => e.muscle_group));
    return Array.from(s).sort();
  }, [entries]);

  const uniqueExercises = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => { if (!map.has(e.exercise_id)) map.set(e.exercise_id, e.exercise_name); });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const filteredEntries = useMemo(() => {
    return entries.filter((e) => {
      if (filterMuscle && e.muscle_group !== filterMuscle) return false;
      if (filterExercise && e.exercise_id !== filterExercise) return false;
      return true;
    });
  }, [entries, filterMuscle, filterExercise]);

  // Progress series: group by date for one exercise
  const progressSeries = useMemo(() => {
    if (!progressExercise) return [];
    return entries
      .filter((e) => e.exercise_id === progressExercise)
      .slice()
      .reverse(); // oldest first
  }, [entries, progressExercise]);

  const requestDelete = (entry) => setConfirmDelete(entry);

  const confirmDeleteNow = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    try {
      await api.delete(`/training-log/${id}`);
      toast.success("Registro eliminado");
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  if (!profile) return <AppShell title="Mi libreta de entrenamiento"><div className="text-[var(--mep-text-muted)]">Cargando...</div></AppShell>;

  const week = summary?.weeks?.[weekIdx];
  const totalExercises = exercisesCatalog.length;

  return (
    <AppShell title="Mi libreta de entrenamiento" subtitle="Registra tu entrenamiento desde las rutinas y consulta tu progreso.">
      <div className="space-y-6" data-testid="training-log-page">
        {/* Header stats */}
        <div className="flex flex-wrap items-center gap-3">
          <div data-testid="training-log-sex-badge" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${sexBadge.color}`}>
            <span className="text-base leading-none">{sexBadge.icon}</span> {sexBadge.label}
          </div>
          <div className="text-xs text-[var(--mep-text-muted)] italic">
            Solo verás ejercicios de tu catálogo. No se mezclan rutinas de mujer y hombre.
          </div>
          <div className="ml-auto flex gap-2">
            <Link
              to={sex === "mujer" ? "/rutinas-mujeres" : "/rutinas-hombres"}
              data-testid="go-to-routines"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--mep-pink)]/40 bg-[rgba(232,61,132,0.08)] text-sm text-[var(--mep-pink)] hover:bg-[rgba(232,61,132,0.15)] transition"
            >
              <Dumbbell size={14} /> Ir a las rutinas
            </Link>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Ejercicios registrados" value={summary?.total_entries ?? 0} icon={BookOpen} testid="stat-entries" />
          <StatCard label="Ejercicios distintos" value={`${summary?.unique_exercises ?? 0}/${totalExercises}`} icon={Dumbbell} testid="stat-unique" />
          <StatCard
            label="Última actualización"
            value={summary?.last_update ? fmtDate(summary.last_update) : "—"}
            icon={Clock} testid="stat-last"
          />
          <StatCard
            label="Semanas visibles"
            value={summary?.weeks?.length ?? 0}
            icon={Calendar} testid="stat-weeks"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[var(--mep-border)]">
          {[
            { id: "semana", label: "Semana / Día" },
            { id: "historial", label: "Historial" },
            { id: "progreso", label: "Progreso de cargas" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2 text-sm transition border-b-2 -mb-px ${
                tab === t.id
                  ? "text-[var(--mep-pink)] border-[var(--mep-pink)]"
                  : "text-[var(--mep-text-muted)] border-transparent hover:text-[var(--mep-text)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Empty state when no entries exist at all */}
        {entries.length === 0 && (
          <div className="mep-card p-10 text-center" data-testid="empty-state">
            <div className="w-14 h-14 rounded-full bg-[rgba(212,175,55,0.08)] border border-[var(--mep-gold)]/40 flex items-center justify-center text-[var(--mep-gold)] mx-auto mb-4">
              <BookOpen size={22} />
            </div>
            <div className="font-display text-xl mb-2">Sin registros todavía</div>
            <p className="text-sm text-[var(--mep-text-muted)] max-w-md mx-auto">
              Cuando registres una serie desde cualquier ejercicio de tu rutina, aparecerá aquí tu progreso, historial y evolución de cargas.
            </p>
            <Link
              to={sex === "mujer" ? "/rutinas-mujeres" : "/rutinas-hombres"}
              className="btn-pink inline-block mt-4 px-4 py-2 text-sm"
            >
              Ir a mis rutinas
            </Link>
          </div>
        )}

        {/* ======= TAB SEMANA ======= */}
        {entries.length > 0 && tab === "semana" && summary && (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap" data-testid="weeks-selector">
              {summary.weeks.map((w, i) => {
                const active = i === weekIdx;
                const startD = localDateFromISODate(w.week_start);
                const endD = localDateFromISODate(w.week_end);
                const label = `${startD.getDate()}-${endD.getDate()} ${endD.toLocaleDateString("es-ES", { month: "short" })}`;
                const count = w.days.reduce((n, d) => n + d.entries.length, 0);
                return (
                  <button
                    key={w.week_start}
                    onClick={() => { setWeekIdx(i); setOpenDay(null); }}
                    data-testid={`week-tab-${i}`}
                    className={`px-4 py-2 rounded-lg text-sm border transition flex-1 min-w-[130px] text-left ${
                      active
                        ? "bg-[rgba(232,61,132,0.08)] border-[var(--mep-pink)] text-[var(--mep-pink)]"
                        : "border-[var(--mep-border)] text-[var(--mep-text-muted)] hover:border-[var(--mep-pink)]/40"
                    }`}
                  >
                    <div className="text-eyebrow">Semana {i + 1}</div>
                    <div className={active ? "font-medium" : ""}>{label}</div>
                    <div className="text-[10px] mt-0.5">{count} registros</div>
                  </button>
                );
              })}
            </div>

            {week && (
              <div className="space-y-2" data-testid="days-list">
                {week.days.map((d) => {
                  const open = openDay === d.date;
                  const hasEntries = d.entries.length > 0;
                  // Group by muscle for the label
                  const muscles = Array.from(new Set(d.entries.map((e) => MUSCLE_LABEL[e.muscle_group] || e.muscle_group)));
                  return (
                    <div
                      key={d.date}
                      data-testid={`day-${d.date}`}
                      className={`rounded-xl border ${hasEntries ? "border-[var(--mep-pink)]/40 bg-[rgba(232,61,132,0.04)]" : "border-[var(--mep-border)] bg-[var(--mep-surface)]"}`}
                    >
                      <button
                        onClick={() => hasEntries && setOpenDay(open ? null : d.date)}
                        className={`w-full flex items-center gap-4 p-4 text-left ${hasEntries ? "" : "opacity-70"}`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${hasEntries ? "bg-[var(--mep-pink)] text-white" : "bg-[var(--mep-elev)] border border-[var(--mep-border)] text-[var(--mep-text-muted)]"}`}>
                          {hasEntries ? "✓" : ""}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium capitalize">{d.day_name}</span>
                            {muscles.length > 0 && (
                              <span className="text-[var(--mep-pink)] text-sm">· {muscles.join(" · ")}</span>
                            )}
                          </div>
                          <div className="text-xs text-[var(--mep-text-muted)] mt-0.5">
                            {localDateFromISODate(d.date).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                            {hasEntries ? (
                              <> · {d.entries.length} ejercicio{d.entries.length === 1 ? "" : "s"}</>
                            ) : (
                              <> · <span className="italic">Sin entrenamiento registrado</span></>
                            )}
                          </div>
                        </div>
                        {hasEntries && <ChevronRight className={`text-[var(--mep-text-muted)] transition-transform ${open ? "rotate-90" : ""}`} size={18} />}
                      </button>
                      {open && hasEntries && (
                        <div className="px-4 pb-4 pt-0 space-y-2 border-t border-[var(--mep-border)]/40" data-testid={`day-entries-${d.date}`}>
                          {d.entries.map((entry) => (
                            <EntryRow
                              key={entry.id}
                              entry={entry}
                              onEdit={() => setEditing(entry)}
                              onDelete={() => requestDelete(entry)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ======= TAB HISTORIAL ======= */}
        {entries.length > 0 && tab === "historial" && (
          <div className="space-y-4">
            <div className="mep-card p-4 flex flex-wrap items-center gap-3" data-testid="filters">
              <Filter size={16} className="text-[var(--mep-gold)]" />
              <select
                data-testid="filter-muscle"
                value={filterMuscle} onChange={(e) => { setFilterMuscle(e.target.value); setFilterExercise(""); }}
                className="bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--mep-pink)]"
              >
                <option value="">Todos los grupos</option>
                {uniqueMuscles.map((m) => <option key={m} value={m}>{MUSCLE_LABEL[m] || m}</option>)}
              </select>
              <select
                data-testid="filter-exercise"
                value={filterExercise} onChange={(e) => setFilterExercise(e.target.value)}
                className="bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--mep-pink)]"
              >
                <option value="">Todos los ejercicios</option>
                {uniqueExercises
                  .filter((x) => !filterMuscle || entries.find((e) => e.exercise_id === x.id)?.muscle_group === filterMuscle)
                  .map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <div className="ml-auto text-xs text-[var(--mep-text-muted)]">
                {filteredEntries.length} resultado{filteredEntries.length === 1 ? "" : "s"}
              </div>
            </div>

            {filteredEntries.length === 0 ? (
              <div className="mep-card p-8 text-center text-sm text-[var(--mep-text-muted)]">
                No hay registros con esos filtros todavía.
              </div>
            ) : (
              <div className="space-y-2" data-testid="historial-list">
                {filteredEntries.map((entry) => (
                  <div key={entry.id} className="mep-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-eyebrow text-[var(--mep-gold)]">
                          {fmtDay(entry.recorded_at)} · {fmtDate(entry.recorded_at)}
                        </div>
                        <div className="font-medium mt-1">{entry.exercise_name}</div>
                        <div className="text-xs text-[var(--mep-text-muted)] uppercase tracking-[0.15em]">
                          {MUSCLE_LABEL[entry.muscle_group] || entry.muscle_group}
                        </div>
                        <div className="text-sm mt-2" data-testid={`stats-${entry.id}`}>
                          {entry.is_bodyweight ? (
                            <span className="font-medium text-[var(--mep-gold)]">Peso corporal</span>
                          ) : entry.cardio_rounds ? (
                            <span className="font-medium">{entry.cardio_rounds} rounds × {entry.cardio_duration_sec}s</span>
                          ) : entry.weight_kg != null ? (
                            <span className="font-medium">{entry.weight_kg} kg</span>
                          ) : null}
                          {!entry.cardio_rounds && (entry.is_bodyweight || entry.weight_kg != null) && <span className="text-[var(--mep-text-muted)]"> · </span>}
                          {entry.cardio_rounds ? (
                            entry.cardio_rest_sec != null && <span className="text-[var(--mep-text-muted)]">descanso {entry.cardio_rest_sec}s</span>
                          ) : (
                            <>
                              <span className="text-[var(--mep-text-muted)]">{entry.sets} series · reps </span>
                              <span>{parseReps(entry.reps_per_set).join("-")}</span>
                            </>
                          )}
                        </div>
                        {entry.note && <div className="text-xs text-[var(--mep-text-muted)] italic mt-1">{entry.note}</div>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditing(entry)}
                          data-testid={`edit-${entry.id}`}
                          className="text-[var(--mep-text-muted)] hover:text-[var(--mep-gold)] p-1"
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => requestDelete(entry)}
                          data-testid={`del-${entry.id}`}
                          className="text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] p-1"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ======= TAB PROGRESO ======= */}
        {entries.length > 0 && tab === "progreso" && (
          <div className="space-y-4">
            <div className="mep-card p-4 flex flex-wrap items-center gap-3">
              <TrendingUp size={16} className="text-[var(--mep-gold)]" />
              <select
                data-testid="progress-exercise-select"
                value={progressExercise} onChange={(e) => setProgressExercise(e.target.value)}
                className="bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--mep-pink)]"
              >
                <option value="">Selecciona un ejercicio…</option>
                {uniqueExercises.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <div className="text-xs text-[var(--mep-text-muted)] ml-auto">
                Progreso: solo sesiones reales registradas.
              </div>
            </div>

            {!progressExercise ? (
              <div className="mep-card p-8 text-center text-sm text-[var(--mep-text-muted)]">
                Elige un ejercicio para ver tu evolución de peso y repeticiones.
              </div>
            ) : progressSeries.length === 0 ? (
              <div className="mep-card p-8 text-center text-sm text-[var(--mep-text-muted)]">
                No hay registros aún para este ejercicio.
              </div>
            ) : (
              <div className="mep-card p-6" data-testid="progress-detail">
                <h3 className="font-display text-xl mb-4">{progressSeries[0].exercise_name}</h3>
                <div className="space-y-2">
                  {progressSeries.map((e, i) => {
                    const prev = i > 0 ? progressSeries[i - 1] : null;
                    const dw = prev && prev.weight_kg != null && e.weight_kg != null
                      ? +(e.weight_kg - prev.weight_kg).toFixed(1) : null;
                    return (
                      <div key={e.id} className="flex items-center gap-4 p-3 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]">
                        <div className="text-xs text-[var(--mep-text-muted)] w-28">
                          {fmtDay(e.recorded_at)}
                          <br />
                          <span className="uppercase tracking-[0.15em] text-[10px]">{fmtDate(e.recorded_at)}</span>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm">
                            {e.cardio_rounds ? (
                              <span className="font-display text-lg text-[var(--mep-text)]">{e.cardio_rounds} rounds × {e.cardio_duration_sec}s</span>
                            ) : e.is_bodyweight ? (
                              <span className="font-display text-lg text-[var(--mep-gold)]">Peso corporal</span>
                            ) : e.weight_kg != null ? (
                              <span className="font-display text-lg text-[var(--mep-text)]">{e.weight_kg} kg</span>
                            ) : <span className="text-[var(--mep-text-muted)]">sin peso</span>}
                            {dw != null && (
                              <span className={`ml-2 text-xs ${dw > 0 ? "text-[var(--mep-gold)]" : dw < 0 ? "text-[var(--mep-pink)]" : "text-[var(--mep-text-muted)]"}`}>
                                {dw > 0 ? "+" : ""}{dw} kg
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[var(--mep-text-muted)]">
                            {e.cardio_rounds
                              ? (e.cardio_rest_sec != null ? `descanso ${e.cardio_rest_sec}s` : "")
                              : `${e.sets} series · reps ${parseReps(e.reps_per_set).join("-")}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Últimos entrenamientos (always on bottom) */}
        {entries.length > 0 && (
          <div className="mep-card p-6" data-testid="latest-trainings">
            <div className="flex items-center gap-2 mb-4">
              <History size={16} className="text-[var(--mep-gold)]" />
              <h3 className="font-display text-xl">Últimos entrenamientos</h3>
            </div>
            <div className="space-y-2">
              {entries.slice(0, 5).map((e) => (
                <div key={e.id} className="flex items-center gap-3 p-3 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]">
                  <Dumbbell size={16} className="text-[var(--mep-pink)]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{e.exercise_name}</div>
                    <div className="text-xs text-[var(--mep-text-muted)]">
                      {fmtDay(e.recorded_at)} · {fmtDate(e.recorded_at)} · {entryStatsText(e)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ===== Edit modal ===== */}
      {editing && (
        <EditTrainingLogModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}

      {/* ===== Delete confirmation ===== */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="confirm-delete-modal"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[var(--mep-surface)] border border-[var(--mep-border)] max-w-sm w-full rounded-2xl p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)] flex-shrink-0">
                <Trash2 size={18} />
              </div>
              <div>
                <div className="font-display text-lg">¿Eliminar este registro?</div>
                <p className="text-sm text-[var(--mep-text-muted)] mt-1">
                  <strong className="text-[var(--mep-text)]">{confirmDelete.exercise_name}</strong>
                  {" · "}{entryStatsText(confirmDelete)} · {fmtDate(confirmDelete.recorded_at)}
                </p>
                <p className="text-xs text-[var(--mep-text-muted)] italic mt-2">
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button
                onClick={() => setConfirmDelete(null)}
                data-testid="confirm-delete-cancel"
                className="px-4 py-2 rounded-lg border border-[var(--mep-border)] text-sm hover:bg-[var(--mep-surface-2)] transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteNow}
                data-testid="confirm-delete-confirm"
                className="px-4 py-2 rounded-lg bg-[var(--mep-pink)] text-white font-medium text-sm hover:brightness-110 transition"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function EditTrainingLogModal({ entry, onClose, onSaved }) {
  const isCardioRounds = !!entry.cardio_rounds;
  const [weight, setWeight] = useState(entry.weight_kg != null ? String(entry.weight_kg) : "");
  const [bodyweight, setBodyweight] = useState(!!entry.is_bodyweight);
  const initialReps = (() => { try { return JSON.parse(entry.reps_per_set); } catch { return [8]; } })();
  const [reps, setReps] = useState((initialReps.length ? initialReps : [8]).map(String));
  const [rounds, setRounds] = useState(entry.cardio_rounds || 5);
  const [durationSec, setDurationSec] = useState(entry.cardio_duration_sec || 120);
  const [restSec, setRestSec] = useState(entry.cardio_rest_sec != null ? entry.cardio_rest_sec : 60);
  const [note, setNote] = useState(entry.note || "");
  const [saving, setSaving] = useState(false);

  const addSet = () => setReps((r) => (r.length >= 10 ? r : [...r, ""]));
  const removeSet = (i) => setReps((r) => (r.length <= 1 ? r : r.filter((_, idx) => idx !== i)));

  const submit = async (e) => {
    e.preventDefault();
    const payload = { note: note || "" };
    if (isCardioRounds) {
      const r = parseInt(rounds, 10);
      const d = parseInt(durationSec, 10);
      const rs = parseInt(restSec, 10);
      if (!(r > 0) || !(d > 0)) { toast.error("Valores inválidos."); return; }
      payload.cardio_rounds = r;
      payload.cardio_duration_sec = d;
      payload.cardio_rest_sec = Number.isFinite(rs) ? rs : 0;
    } else {
      const parsed = reps.map((v) => parseInt(v, 10)).filter((n) => Number.isFinite(n) && n > 0);
      if (parsed.length === 0) { toast.error("Debe quedar al menos una serie válida."); return; }
      payload.reps_per_set = parsed;
      if (bodyweight) {
        payload.is_bodyweight = true;
      } else {
        payload.is_bodyweight = false;
        payload.weight_kg = weight === "" ? null : parseFloat(weight);
      }
    }
    setSaving(true);
    try {
      await api.patch(`/training-log/${entry.id}`, payload);
      toast.success("Registro actualizado");
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="edit-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--mep-surface)] border border-[var(--mep-border)] w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-eyebrow text-[var(--mep-gold)]">Editar registro</div>
            <h3 className="font-display text-xl mt-1">{entry.exercise_name}</h3>
            <div className="text-xs text-[var(--mep-text-muted)] uppercase tracking-[0.15em] mt-0.5">
              {MUSCLE_LABEL[entry.muscle_group] || entry.muscle_group} · {fmtDate(entry.recorded_at)}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--mep-text-muted)] hover:text-white p-1" data-testid="edit-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isCardioRounds ? (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Rounds</label>
                <input data-testid="edit-rounds" type="number" min="1" max="99" value={rounds} onChange={(ev) => setRounds(ev.target.value)}
                  className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-gold)]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Duración (s)</label>
                <input data-testid="edit-duration" type="number" min="5" step="5" value={durationSec} onChange={(ev) => setDurationSec(ev.target.value)}
                  className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-gold)]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Descanso (s)</label>
                <input data-testid="edit-rest" type="number" min="0" step="5" value={restSec} onChange={(ev) => setRestSec(ev.target.value)}
                  className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-gold)]" />
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Modo</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setBodyweight(false)} data-testid="edit-mode-external"
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border transition ${!bodyweight ? "bg-[var(--mep-pink)] text-white border-[var(--mep-pink)]" : "border-[var(--mep-border)] text-[var(--mep-text-muted)]"}`}>Peso externo</button>
                  <button type="button" onClick={() => setBodyweight(true)} data-testid="edit-mode-bodyweight"
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border transition ${bodyweight ? "bg-[var(--mep-gold)] text-black border-[var(--mep-gold)]" : "border-[var(--mep-border)] text-[var(--mep-text-muted)]"}`}>Peso corporal</button>
                </div>
              </div>

              {!bodyweight && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Peso (kg)</label>
                  <input data-testid="edit-weight" type="number" step="0.5" value={weight} onChange={(ev) => setWeight(ev.target.value)}
                    className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-gold)]" />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)]">Reps por serie</label>
                  <button type="button" onClick={addSet} className="text-xs text-[var(--mep-pink)] hover:underline">+ Añadir serie</button>
                </div>
                <div className="space-y-2">
                  {reps.map((v, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-16 text-[11px] uppercase tracking-[0.14em] text-[var(--mep-text-muted)]">Serie {i + 1}</div>
                      <input data-testid={`edit-reps-${i}`} type="number" min="1" max="999" value={v}
                        onChange={(ev) => { const copy = [...reps]; copy[i] = ev.target.value; setReps(copy); }}
                        className="flex-1 bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-gold)]" />
                      {reps.length > 1 && (
                        <button type="button" onClick={() => removeSet(i)} className="text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] p-1">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Nota (opcional)</label>
            <input data-testid="edit-note" type="text" value={note} onChange={(ev) => setNote(ev.target.value)}
              className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-gold)]" />
          </div>

          <button type="submit" disabled={saving} data-testid="edit-submit"
            className="w-full py-3 rounded-lg bg-[var(--mep-gold)] text-black font-semibold hover:brightness-110 transition disabled:opacity-50">
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </form>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="mep-card p-4">
    <div className="flex items-center gap-2 text-eyebrow text-[var(--mep-gold)] mb-1">
      <Icon size={14} /> {label}
    </div>
    <div className="font-display text-xl">{value}</div>
  </div>
);

const EntryRow = ({ entry, onEdit, onDelete }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--mep-surface)] border border-[var(--mep-border)]">
    <div className="w-8 h-8 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)]">
      <Dumbbell size={14} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm truncate">{entry.exercise_name}</div>
      <div className="text-xs text-[var(--mep-text-muted)]">
        {MUSCLE_LABEL[entry.muscle_group] || entry.muscle_group} · {entryStatsText(entry)}
      </div>
      {entry.note && <div className="text-[11px] text-[var(--mep-text-muted)] italic mt-0.5 truncate">{entry.note}</div>}
    </div>
    <button
      onClick={onEdit}
      data-testid={`edit-${entry.id}`}
      className="text-[var(--mep-text-muted)] hover:text-[var(--mep-gold)] p-1"
      title="Editar"
    >
      <Pencil size={14} />
    </button>
    <button
      onClick={onDelete}
      data-testid={`del-${entry.id}`}
      className="text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] p-1"
      title="Eliminar"
    >
      <Trash2 size={14} />
    </button>
  </div>
);
